import { useGetNotificationsQuery } from "@/redux/feature/notification/notificationApi";
import { useAppSelector } from "@/redux/hooks/hooks";
import { getSocket, initiateSocket } from "@/services/websocket/socket";
import { TNotification } from "@/types/types"; // Ensure this type is defined properly
import { playNotificationSound } from "@/utils/playNotificationSound";
import { pushNotification } from "@/utils/pushNotification";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import toast from "react-hot-toast";

export type NotificationCategory = "all" | "unread" | "action_required";

// Define the context type properly
type NotificationContextType = {
  notifications: TNotification[];
  category: NotificationCategory;
  setCategory: React.Dispatch<React.SetStateAction<NotificationCategory>>;
  totalUnreadNotifications: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  meta: {
    page: unknown;
    limit: unknown;
    totalPages: number;
    totalUnreadNotifications: number;
  };
};

// Create a NotificationContext with a default empty object
const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<TNotification[]>([]);

  const user = useAppSelector((state) => state.auth);

  const [category, setCategory] = useState<NotificationCategory>("unread");
  const [page, setPage] = useState(1);

  const { data, refetch } = useGetNotificationsQuery([
    { name: "email", value: user?.user?.email },
    { name: "limit", value: 10 },
    { name: "category", value: category },
    { name: "page", value: page },
  ]);
  const meta = data?.data?.meta;
  // Update notifications state whenever data changes
  useEffect(() => {
    if (data?.data?.data) {
      setNotifications(data.data.data);
    }
  }, [data]);

  // Function to check if the tab is active
  const isTabActive = () => !document.hidden;

  const totalUnreadNotifications = data?.data?.meta?.totalUnreadNotifications;

  // Handle visibility change for checking if tab is active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("Tab is not active.");
      } else {
        console.log("Tab is active.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);
  // const requestNotificationPermission = async () => {
  //   const permission = await Notification.requestPermission();
  //   console.log(permission);
  //   if (permission === "granted") {
  //     toast.success("Notification permission granted!");
  //   } else if (permission === "denied") {
  //     toast.error("Notification permission denied!");
  //   }
  // };

  // Request permission on first load (optional)
  // useEffect(() => {
  //   if (Notification.permission === "default") {
  //     toast((t) => (
  //       <span>
  //         Enable notifications to stay updated!
  //         <button
  //           onClick={() => {
  //             requestNotificationPermission();
  //             toast.dismiss(t.id);
  //           }}
  //           className="ml-2 p-1 border rounded"
  //         >
  //           Enable
  //         </button>
  //       </span>
  //     ));
  //   }
  // }, []);
  // Socket connection and notification handling

  useEffect(() => {
    if (!user?.user?.email) {
      return;
    }

    initiateSocket({
      userEmail: user?.user?.email as string,
      token: user.token as string,
    });
    const socket = getSocket();
    socket?.connect();
    if (socket) {
      socket.on("notification", (data: TNotification) => {
        console.log("Received notification:", data);
        refetch();
        playNotificationSound();
        if (!isTabActive()) {
          pushNotification(data.title);
        } else {
          toast.success(data.title);
        }
      });

      // Cleanup when component unmounts
      return () => {
        socket.off("notification");
      };
    }
  }, [user]);
  return (
    <NotificationContext.Provider
      value={{
        meta,
        page,
        setPage,
        notifications,
        totalUnreadNotifications,
        setCategory,
        category,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }

  return context;
};
